import React, { useState, useEffect } from 'react';
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material';
import EncryptKeyTableHeader from 'components/encrypt-key/encrypt-key-table-header';
import { makeStyles, withStyles } from '@mui/styles';
import moment from 'moment';
import PropTypes from 'prop-types';
import FormControl from '@mui/material/FormControl';
import ModifyEncryptKeyModal from 'components/encrypt-key/modify-encrypt-key-modal';
import { useDispatch } from 'react-redux';
import { changeSelectedModifyEncryptKey } from 'reducers/encrypt-key/action';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const StyledTableCell = withStyles(() => ({
  body: {
    fontSize: 14,
    textAlign: 'center !important',
  },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
  root: {
    '&:hover': {
      backgroundColor: 'rgb(221,243,255)',
    },
    cursor: 'pointer',
  },
}))(TableRow);

const useStyles = makeStyles({
  table: {
    width: '100%',
    minWidth: 700,
  },
  tablePagination: {
    zIndex: 9999,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rgb(221,243,255)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
});

function EncryptKeyTable({
  data,
  onDeleteEncryptKey,
  onChangeModifyEncryptKeys,
  onChangeModifyEncryptKeyModalStatus,
  isVisibleModifyEncryptKeyModal,
}) {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [originStatus, setOriginStatus] = useState(data);
  const [selectStatus, setSelectStatus] = useState(data);
  const dispatch = useDispatch();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleRadioButton = (row, value) => {
    const { uid } = row;
    // 변경된 값 = encryptKey
    const encryptKey = {
      ...row,
      isActive: value,
    };
    // selectStatus 리스트에 uid 체크
    if (selectStatus.findIndex(x => x.uid === uid) > -1) {
      setSelectStatus(prevState =>
        prevState.map(status =>
          status.uid === uid ? { ...encryptKey } : { ...status },
        ),
      );
    } else {
      setSelectStatus(prevState => prevState.concat(encryptKey));
    }
  };

  const onClickTableRow = row => {
    dispatch(changeSelectedModifyEncryptKey(row));
    onChangeModifyEncryptKeyModalStatus(true);
  };

  useEffect(() => {
    setOriginStatus(data);
    setSelectStatus(data);
  }, [data]);

  useEffect(() => {
    onChangeModifyEncryptKeys(selectStatus);
  }, [selectStatus]);

  return (
    <>
      <TableContainer>
        <Table
          className={classes.table}
          size="small"
          aria-label="customized table"
        >
          <EncryptKeyTableHeader
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {selectStatus.length > 0 &&
              stableSort(selectStatus, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, key) => (
                  <StyledTableRow
                    key={row.uid}
                    onClick={() => onClickTableRow(row)}
                  >
                    <StyledTableCell>
                      {page * rowsPerPage + key + 1}
                    </StyledTableCell>
                    <StyledTableCell>{row.encryptKey}</StyledTableCell>
                    <StyledTableCell>{row.encryptKeyVersion}</StyledTableCell>
                    <StyledTableCell>
                      {moment(row.createdAt).format('YYYY.MM.DD HH:mm')}
                    </StyledTableCell>
                    <StyledTableCell>
                      {originStatus.find(item => item.uid === row.uid)
                        .isActive === 'Y'
                        ? '활성'
                        : '비활성'}
                    </StyledTableCell>
                    <StyledTableCell>
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          aria-label={row.uid}
                          name="row-radio-buttons-group"
                        >
                          <FormControlLabel
                            checked={row.isActive === 'Y'}
                            value="Y"
                            control={<Radio size="small" />}
                            labelPlacement="start"
                            label="활성"
                            onClick={e => e.stopPropagation()}
                            onChange={e =>
                              handleRadioButton(row, e.target.value)
                            }
                          />
                          <FormControlLabel
                            checked={row.isActive === 'N'}
                            value="N"
                            control={<Radio size="small" />}
                            labelPlacement="start"
                            label="비활성"
                            onClick={e => e.stopPropagation()}
                            onChange={e =>
                              handleRadioButton(row, e.target.value)
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        style={{ marginLeft: 15 }}
                        onClick={e => {
                          e.stopPropagation();
                          onDeleteEncryptKey(row.uid);
                        }}
                      >
                        삭제
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[20, 50, 100]}
        component="div"
        count={selectStatus.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className={classes.tablePagination}
        onPageChange={handleChangePage}
      />
      <ModifyEncryptKeyModal
        isVisibleModifyEncryptKeyModal={isVisibleModifyEncryptKeyModal}
        onChangeModifyEncryptKeyModalStatus={
          onChangeModifyEncryptKeyModalStatus
        }
      />
    </>
  );
}

EncryptKeyTable.propTypes = {
  data: PropTypes.array.isRequired,
  onDeleteEncryptKey: PropTypes.func.isRequired,
  onChangeModifyEncryptKeys: PropTypes.func.isRequired,
  onChangeModifyEncryptKeyModalStatus: PropTypes.func,
  isVisibleModifyEncryptKeyModal: PropTypes.bool,
};

export default EncryptKeyTable;
